import { useState } from 'react';
import { SectionHeadingContainer } from '@/components/SectionHeadingContainer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import RecruitmentModal from './RecruitmentModal';

/** お問い合わせとエントリーを同一セクション（id=entry）で扱う */
export default function CTASection() {
  const ref = useScrollAnimation();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section
      id="entry"
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: '#0B1A2A', paddingTop: '65px', paddingBottom: '130px' }}
      aria-label="お問い合わせ・エントリー"
    >
      <div
        className="pointer-events-none absolute left-0 top-0 bottom-0 w-px opacity-40"
        style={{
          background:
            'linear-gradient(180deg, transparent 0%, rgba(168,205,217,0.5) 20%, rgba(168,205,217,0.5) 80%, transparent 100%)',
        }}
        aria-hidden={true}
      />

      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[min(100vw,880px)] -translate-x-1/2 rounded-full opacity-45 blur-3xl"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(61, 107, 140, 0.32) 0%, transparent 72%)',
        }}
        aria-hidden={true}
      />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-8 lg:px-16">
        {/* 英字：ENTRY と CONTACT を並記して同一セクションであることを示す */}
        <div className="fade-in-up mb-6">
          <span
            className="section-label-en font-montserrat font-light uppercase"
            style={{ color: '#A8CDD9' }}
          >
            ENTRY · CONTACT
          </span>
        </div>

        <SectionHeadingContainer>
          <h2 className="section-heading section-heading--cta fade-in-up delay-1 mb-12 text-center text-white lg:mb-12">
            <span className="block">人生を、自分で切り拓く。</span>
            <span className="mt-2 block sm:mt-3">その一歩を、ここから。</span>
          </h2>
        </SectionHeadingContainer>

        <div
          className="fade-in-up delay-2 mx-auto h-px w-10"
          style={{ backgroundColor: '#B89B4A' }}
          aria-hidden={true}
        />

        <p
          className="fade-in-up delay-2 mx-auto mt-10 max-w-xl break-keep px-1 text-center text-[13px] font-light leading-relaxed sm:text-sm md:mt-12 md:text-[15px] md:leading-[2]"
          style={{
            color: 'rgba(168, 205, 217, 0.85)',
            fontFamily: "'Noto Sans JP', sans-serif",
            fontWeight: 300,
            lineBreak: 'strict',
            wordBreak: 'keep-all',
          }}
        >
          <span className="block">興味を持ったら、気軽にご連絡ください。</span>
          <span className="mt-2 block sm:mt-2.5">あなたの一歩をお待ちしています。</span>
        </p>

        <p
          className="fade-in-up delay-2 mx-auto mt-6 max-w-xl text-center text-[12px] font-light leading-relaxed sm:text-[13px]"
          style={{
            color: 'rgba(168, 205, 217, 0.55)',
            fontFamily: "'Noto Sans JP', sans-serif",
            fontWeight: 300,
          }}
        >
          採用・サービスに関するご質問は、各チャネルよりお問い合わせください。
        </p>

        <div className="fade-in-up delay-3 mx-auto mt-12 flex w-full max-w-md flex-col gap-3 sm:max-w-lg">
          <a
            href="https://lin.ee/ZHvBTbt"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-btn-gold flex w-full items-center justify-center gap-3 px-8 py-4 font-light text-white"
            style={{
              backgroundColor: '#B89B4A',
              fontSize: 'clamp(13px, 1.3vw, 15px)',
              fontFamily: "'Noto Sans JP', sans-serif",
              fontWeight: 300,
              letterSpacing: '0.1em',
            }}
          >
            <i className="ri-line-fill text-lg" aria-hidden={true} />
            LINEでエントリーする
          </a>
          <a
            href="https://www.instagram.com/inc.universe?igsh=MTRhaXYwbTdyY3R2cQ%3D%3D&utm_source=qr"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-btn-outline flex w-full items-center justify-center gap-3 px-8 py-4 font-light text-white"
            style={{
              border: '1px solid rgba(255,255,255,0.45)',
              fontSize: 'clamp(13px, 1.3vw, 15px)',
              fontFamily: "'Noto Sans JP', sans-serif",
              fontWeight: 300,
              letterSpacing: '0.1em',
            }}
          >
            <i className="ri-instagram-line text-lg" aria-hidden={true} />
            Instagramを見る
          </a>
        </div>

        <p
          className="fade-in-up delay-3 mt-3 text-center text-[11px] font-light"
          style={{ color: 'rgba(168, 205, 217, 0.42)', fontFamily: "'Noto Sans JP', sans-serif" }}
        >
          外部サービスへ移動します
        </p>

        {/* 募集要項（同一セクション内） */}
        <div className="fade-in-up delay-4 mt-12 flex justify-center sm:mt-14">
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="btn-pill-outline inline-flex items-center justify-center gap-2 px-10 py-3.5 text-sm font-light tracking-widest"
            style={{
              color: '#B89B4A',
              border: '1px solid #B89B4A',
              fontFamily: "'Noto Sans JP', sans-serif",
              letterSpacing: '0.12em',
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(184,155,74,0.12)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            }}
          >
            <i className="ri-file-list-3-line text-lg" aria-hidden={true} />
            募集要項を見る
          </button>
        </div>
      </div>

      <RecruitmentModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}
